import {
    Box,
    Button,
    Chip,
    Divider,
    Icon,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
} from "chart.js";
import { useContext, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
    AccountReportDto,
    GenerateReportInput,
    ReportResultDto,
    ReportService,
} from "../../../api/psc";
import { PageHeader, Spinner } from "../../../components/exports";
import {
    ReportChartColors,
    ReportOptions,
} from "../../../constants/ReportConstants";
import { DialogContext } from "../../../contexts/DialogueContext";
import { toJalaliDate } from "../../../utils/date";
import ReportFilter from "../components/ReportFilter";
import ReportFilterDialog from "../dialogs/ReportFilterDialog";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// updated
export default function GenerateReport(props: {
    selectedReport: AccountReportDto;
    resetReport: () => void;
}) {
    const [isFetching, setIsFetching] = useState(true);
    const [reportResult, setReportResult] = useState<ReportResultDto | null>(
        null
    );
    const { openDialog } = useContext(DialogContext);

    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));

    useEffect(() => {
        ReportService.generateReport(props.selectedReport.type, "1", {
            filterArguments: props.selectedReport.filters.map((f, i) => {
                return { arguments: [], index: i };
            }),
        }).then((r) => {
            setReportResult(r);
            setIsFetching(false);
        });
    }, []);

    const openFilter = () => {
        openDialog(
            <ReportFilterDialog
                isFetching={isFetching}
                report={props.selectedReport}
                applyFilter={generateResult}
            />
        );
    };

    const generateResult = (input: GenerateReportInput) => {
        setIsFetching(true);
        ReportService.generateReport(
            props.selectedReport.type,
            "1",
            input
        ).then((r) => {
            setReportResult(r);
            setIsFetching(false);
        });
    };

    const labelsAlt = reportResult?.entries
        .map((e) => [e.date])
        .reduce((a, b) => {
            return a.concat(b.filter((i) => !a.includes(i)));
        }, []);

    const labels = labelsAlt?.map((e) => toJalaliDate(e));

    const distinctGroupingValues = reportResult?.entries
        .map((i) => [i.groupings])
        .reduce((a, b) => {
            return a.concat(b.filter((i) => !a.includes(i)));
        }, []);

    const data = {
        labels: labels,
        datasets: distinctGroupingValues?.map((g, i) => {
            return {
                label: props.selectedReport.filters[0].items.find(
                    (i) => i.id === g
                )?.title ?? "-",
                data: labelsAlt?.map(
                    (l) =>
                        reportResult.entries.find(
                            (e) => e.date === l && e.groupings === g
                        )?.count
                ),
                backgroundColor: ReportChartColors[i],
                maxBarThickness: 10,
            };
        }),
    };

    return (
        <Box>
            <PageHeader
                title={props.selectedReport.title}
                action={
                    <Stack direction="row" spacing={1}>
                        <Button
                            size={isLargeScreen ? "medium" : "small"}
                            variant="contained"
                            color="primary"
                            onClick={props.resetReport}
                            endIcon={<Icon>arrow_back</Icon>}
                        >
                            تغییر گزارش
                        </Button>
                        {!isLargeScreen && (
                            <Button
                                size={isLargeScreen ? "medium" : "small"}
                                variant="contained"
                                color="secondary"
                                onClick={openFilter}
                                endIcon={<Icon>filter_alt</Icon>}
                            >
                                فیلتر
                            </Button>
                        )}
                    </Stack>
                }
            />

            <Stack direction="row" spacing={2} alignItems="start">
                <Paper sx={{ p: 2, display: { xs: "none", md: "block" } }}>
                    <Typography>فیلتر گزارش</Typography>

                    <Divider sx={{ my: 2 }} />

                    <ReportFilter
                        isFetching={isFetching}
                        report={props.selectedReport}
                        applyFilter={generateResult}
                    />
                </Paper>

                {isFetching && <Spinner />}

                {!isFetching && reportResult && (
                    <Stack sx={{ width: "100%" }}>
                        <Paper sx={{ p: 2 }}>
                            <Bar options={ReportOptions} data={data} />
                        </Paper>

                        <Stack direction="row" sx={{ mt: 2 }} spacing={2}>
                            <Box sx={{ flexGrow: 1 }} />

                            <Chip
                                variant="outlined"
                                label={
                                    <Stack direction="row" spacing={1}>
                                        <Typography color="text.secondary">
                                            تعداد:{" "}
                                        </Typography>
                                        <Typography color="primary">
                                            {reportResult.filterCount}
                                        </Typography>
                                    </Stack>
                                }
                            />
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}
